<template>
  <v-card outlined class="note-card d-flex flex-column">
    <v-card-text class="pa-2 flex-grow-1 d-flex flex-column">
      <v-row no-gutters class="flex-grow-1">
        <!-- Left column: metadata -->
        <v-col cols="12" md="3" class="pr-md-3 mb-2 mb-md-0 meta-column">
          <v-chip
            :color="typeInfo?.color || 'grey'"
            :text-color="typeInfo?.onColor || 'white'"
            small
            label
            class="mb-2"
            aria-label="Note type"
          >
            <v-icon left small>{{ typeInfo?.image || 'mdi-note-outline' }}</v-icon>
            {{ typeLabel }}
          </v-chip>

          <div class="text-caption font-weight-bold">
            <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
            {{ $t('note.validity') }}:
            <v-chip v-if="isPast()" x-small outlined class="ml-1">{{ $t('note.expired') }}</v-chip>
          </div>

          <div class="text-caption date-grid">
            <template v-if="note.beginning_at">
              <strong>{{ $t('note.from') }}:</strong>
              <span>{{ note.beginning_at | dateMediumTimeShort }}</span>
            </template>
            <strong :class="{ 'text--secondary': !note.expires_at }">{{ $t('note.to') }}:</strong>
            <span :class="{ 'text--secondary': !note.expires_at }">
              {{
                note.expires_at
                  ? $options.filters.dateMediumTimeShort(note.expires_at)
                  : $t('note.no_expiry')
              }}
            </span>
          </div>
        </v-col>

        <!-- Right column: content + actions -->
        <v-col cols="12" md="9" class="pl-md-3 d-flex flex-column">
          <div>
            <site-link dense inactive align="left" :site="note.site"/>
          </div>

          <div class="d-flex align-baseline flex-grow-1">
            <div
              :id="noteMessageId"
              ref="messageEl"
              class="body-2 note-text flex-grow-1"
              :class="{
                'note-text--expanded': messageExpanded,
                'note-text--clickable': isClickable,
              }"
              role="region"
              aria-label="Note content"
              :tabindex="isInteractive ? 0 : -1"
              @click="handleExpandClick"
            >
              {{ note.content?.message || $t('note.no_content') }}
            </div>
            <v-btn
              v-if="showExpandButton"
              text
              x-small
              class="text-decoration-underline text-lowercase pa-1 ml-1"
              :aria-expanded="String(messageExpanded)"
              :aria-controls="noteMessageId"
              @click="toggleExpanded"
            >
              {{ messageExpanded ? $t('button.less') : $t('button.more') }}
            </v-btn>
          </div>

          <v-divider />

          <!-- Footer: author info + actions -->
          <div class="d-flex align-center justify-space-between">
            <span class="d-flex align-center">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <span class="d-flex align-center caption" v-bind="attrs" v-on="on">
                    <v-icon small class="mr-1">mdi-account-clock</v-icon>
                    {{ note.created_by | userName }}
                  </span>
                </template>
                <span class="caption">
                  <strong>{{ $t('note.created') }}:</strong>
                  {{ note.created_at | formatDistanceWithPrefix }}
                </span>
              </v-tooltip>

              <v-tooltip bottom v-if="note.content?.reported_by">
                <template #activator="{ on, attrs }">
                  <span class="d-flex align-center caption ml-3" v-bind="attrs" v-on="on">
                    <v-icon small class="mr-1">mdi-phone</v-icon>
                    {{ note.content?.reported_by | userName }}
                  </span>
                </template>
                <span class="caption">
                  <strong>{{ $t('note.reported') }}:</strong>
                  {{ note.content?.reported_by.created_at | formatDistanceWithPrefix }}
                </span>
              </v-tooltip>
            </span>
            <div class="d-flex">
              <v-tooltip v-if="editable" bottom>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    aria-label="Edit note"
                    v-on="on"
                    @click="editNote"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('button.edit') }}</span>
              </v-tooltip>

              <v-tooltip v-if="deletable" bottom>
                <template #activator="{ on }">
                  <v-btn
                    class="ml-2"
                    icon
                    small
                    color="error"
                    aria-label="Delete note"
                    v-on="on"
                    @click="deleteNote"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('button.delete') }}</span>
              </v-tooltip>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
  import { Note, NoteType } from '@/store/modules/note'
  import SiteService from '@/services/site.service'

  export default {
    name: 'Note',

    props: {
      note: {
        type: Object,
        required: true,
      },
      editable: {
        type: Boolean,
        default: false,
      },
      deletable: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      messageExpanded: false,
      messageOverflowing: false,
    }),

    computed: {
      typeInfo() {
        return Note.get(this.note?.category) || {}
      },

      typeLabel() {
        if (!this.typeInfo?.name) return 'Note'
        if (this.note?.category === NoteType.GENERAL) return 'Note'
        return this.typeInfo.name
      },

      noteMessageId() {
        return `note-message-${this.note?.id || 'unknown'}`
      },

      showExpandButton() {
        return this.messageOverflowing || this.messageExpanded
      },

      isClickable() {
        return this.messageOverflowing && !this.messageExpanded
      },

      isInteractive() {
        return this.messageOverflowing || this.messageExpanded
      },
    },

    watch: {
      'note.content.message': 'checkOverflow',
    },

    mounted() {
      this.checkOverflow()
      window.addEventListener('resize', this.checkOverflow)
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.checkOverflow)
    },

    methods: {
      isPast() {
        return Note.isPast(this.note)
      },

      editNote() {
        if (this.editable) {
          this.$router.push({ name: 'Edit Note', params: { noteId: this.note.id } })
        }
      },

      async deleteNote() {
        if (!this.deletable) return

        const confirmed = await this.$dialog.confirm({
          text: this.$t('note.delete.confirm', [this.note.site?.name || '']),
          title: '',
          actions: {
            false: this.$t('button.no'),
            true: { color: 'error', text: this.$t('button.delete') },
          },
        })

        if (confirmed) {
          const noteToDelete = {
            ...this.note,
            site_id: this.note.site.id,
          }
          await SiteService.finishNote(noteToDelete)
          this.$emit('deleted', this.note)
        }
      },

      toggleExpanded() {
        this.messageExpanded = !this.messageExpanded
        this.$nextTick(this.checkOverflow)
      },

      handleExpandClick() {
        if (this.isClickable) {
          this.messageExpanded = true
        }
      },

      checkOverflow() {
        this.$nextTick(() => {
          const el = this.$refs.messageEl
          const message = this.note?.content?.message || ''

          // Handle explicit newlines as needing expansion
          if (message.includes('\n')) {
            this.messageOverflowing = true
            return
          }

          if (!el) {
            this.messageOverflowing = false
            return
          }

          // Check horizontal overflow when white-space: nowrap is applied
          this.messageOverflowing = el.scrollWidth > el.clientWidth + 1
        })
      },
    },
  }
</script>

<style scoped>
  /* Make the card fill height and use flex layout */
  .note-card {
    height: 100%;
  }

  /* Left column border for desktop */
  .meta-column {
    border-right: 1px solid var(--v-divider-base, rgba(0, 0, 0, 0.12));
  }

  /* Date grid for aligned From/To labels */
  .date-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 4px;
    row-gap: 2px;
  }

  /* Note text truncation and expansion */
  .note-text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.4;
    min-width: 0;
  }

  .note-text--expanded {
    white-space: pre-wrap;
  }

  .note-text--clickable {
    cursor: pointer;
  }

  .note-text--clickable:hover {
    background-color: var(--v-hover-base, rgba(0, 0, 0, 0.04));
    border-radius: 4px;
  }

  /* Mobile: switch border from right to bottom */
  @media (max-width: 960px) {
    .meta-column {
      border-right: none;
      border-bottom: 1px solid var(--v-divider-base, rgba(0, 0, 0, 0.12));
      padding-bottom: 8px;
    }
  }
</style>
